import { TextWithUnsureName } from "@/components/molecules/unsure-name-mark";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/molecules/tables/data-table";
import { useEvents } from "@/features/calendar/hooks/use-events";
import type { CalendarEvent } from "@/features/calendar/types";
import type { Customer } from "@/features/customers/types";
import type { ColumnDef } from "@tanstack/react-table";
import { CalendarIcon, MapPinIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";

/** An appointment is behind us once it has ended, not once it has started. */
const isPastEvent = (event: CalendarEvent, now = Date.now()) =>
  event.end_time < now;

interface CustomerEventsTableProps {
  customer: Customer;
  limit?: number;
}

export function CustomerEventsTable({
  customer,
  limit,
}: CustomerEventsTableProps) {
  const { t } = useTranslation("customers");
  const { format } = useDateFormat();
  const { company: clientId } = useParams<{ company: string }>();
  const navigate = useNavigate();

  // Fetch events for a reasonable time range (past 30 days to future 90 days)
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Start of today
  const thirtyDaysAgo = now.getTime() - 30 * 24 * 60 * 60 * 1000;
  const ninetyDaysAhead = now.getTime() + 90 * 24 * 60 * 60 * 1000;

  // Use backend filtering by customer_id
  const { data: events = [], isLoading } = useEvents({
    customer_id: customer.id,
    timeMin: thirtyDaysAgo,
    timeMax: ninetyDaysAhead,
  });

  // Sort and limit on frontend (backend already filters by customer_id)
  const displayEvents = useMemo(() => {
    // What is still to come first, soonest first; then what already took place,
    // most recent first — so the limit never cuts an upcoming appointment to
    // make room for last month's.
    const now = Date.now();
    const upcoming = events
      .filter((e) => !isPastEvent(e, now))
      .sort((a, b) => a.start_time - b.start_time);
    const past = events
      .filter((e) => isPastEvent(e, now))
      .sort((a, b) => b.start_time - a.start_time);

    const sorted = [...upcoming, ...past];
    return limit ? sorted.slice(0, limit) : sorted;
  }, [events, limit]);

  const columns: ColumnDef<CalendarEvent>[] = [
    {
      accessorKey: "summary",
      header: t("events.event"),
      cell: ({ row }) => {
        const event = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="bg-muted rounded-lg p-2">
              <CalendarIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <div className="font-medium">
                <TextWithUnsureName
                  text={event.summary}
                  unsureName={event.unsure_customer_name}
                />
              </div>
              {event.description && (
                <div className="text-xs text-muted-foreground truncate max-w-[300px]">
                  {event.description}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "start_time",
      header: t("events.dateTime"),
      cell: ({ row }) => {
        const event = row.original;
        if (event.is_all_day) {
          return (
            <div className="text-sm">
              {format(event.start_time, "PPP")}
              <Badge variant="outline" className="ml-2 text-xs">
                {t("events.allDay")}
              </Badge>
            </div>
          );
        }
        return (
          <div className="text-sm">
            <div>{format(event.start_time, "PPP")}</div>
            <div className="text-xs text-muted-foreground">
              {format(event.start_time, "p")} - {format(event.end_time, "p")}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "location",
      header: t("events.location"),
      cell: ({ row }) => {
        const location = row.original.location;
        return location ? (
          <div className="flex items-center gap-2 text-sm">
            <MapPinIcon className="h-4 w-4 text-muted-foreground" />
            {location}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      accessorKey: "status",
      header: t("events.status"),
      cell: ({ row }) => {
        const event = row.original;
        const isPast = isPastEvent(event);
        const variant = isPast ? "secondary" : "default";
        return (
          <Badge variant={variant}>
            {isPast
              ? t("events.past")
              : event.status
                ? t(`events.statuses.${event.status}`, event.status)
                : t("events.scheduled")}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: () => (
        <div className="text-right">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/${clientId}/calendar`)}
          >
            {t("actions.view")}
          </Button>
        </div>
      ),
    },
  ];

  if (displayEvents.length === 0 && !isLoading) {
    return (
      <div className="text-center py-8">
        <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">{t("events.noEvents")}</p>
      </div>
    );
  }

  return (
    <DataTable
      variant="embedded"
      columns={columns}
      data={displayEvents}
      isLoading={isLoading}
      total={displayEvents.length}
      isRowMuted={(event) => isPastEvent(event)}
    />
  );
}
